import React, { useEffect, useState } from 'react';
import Modal from 'components/Modal';
import { Alert, Button, Card, Checkbox, Col, Form, Result, Row, Space, Input } from 'antd';
import { gql, useApolloClient } from '@apollo/client';
import { useMutation } from 'hooks/apollo';
import useGlobalContext from 'hooks/useGlobalContext';
import useUser from 'hooks/useUser';
import { createOrder, getCalculatedOrder } from 'shared/utils/order';
import { isMidTermDowngradeBlocked } from 'shared/utils/license';
import { DataSourceType, LicenseInterval } from 'shared/prisma/types';
import useDataSource from 'hooks/useDataSource';
import SetupPaymentButton from 'components/SetupPaymentButton';
import { dayjs } from 'shared/utils/date';
import { TERMS_OF_SERVICE_NO_PDA } from 'shared/constants/urls';
import { currencyFormat } from 'shared/utils/formatter';
import Order from 'components/Order';
import LegacyLicenseForm from './LegacyLicenseForm';
import ModuleLicenseForm from './ModuleLicenseForm';
import ModuleLicenseV2Form from './ModuleLicenseV2Form';
import ModuleLicenseV3Form from './ModuleLicenseV3Form';
import OrderForm from './OrderForm';

const CHANGE_LICENSE = gql`
  mutation($input: DataSourceLicenseInput!, $syncDeal: Boolean, $orderConfirmation: Boolean) {
    changeDataSourceLicense(input: $input, syncDeal: $syncDeal, orderConfirmation: $orderConfirmation) {
      id
      startDate
      endDate
      renewalDate
      interval
    }
  }
`;

const VALIDATE_COUPON = gql`
  query ValidateCoupon($code: String!, $customerId: ID!) {
    validateCoupon(code: $code, customerId: $customerId) {
      id
      code
      description
      discountAmount
      discountType
      limitPerCustomer
    }
  }
`;

const DEFAULT_LICENSE_TYPE = {
  [DataSourceType.Cx]: 'v1',
  [DataSourceType.Website]: 'v3',
};

const STEPS = {
  SELECT_LICENSE_TYPE: 'Select license type',
  CONFIGURE_LICENSE: 'Configure license',
  CUSTOMIZE_ORDER: 'Customize order',
  CUSTOMIZE_RENEWAL: 'Customize renewal',
  ORDER_CONFIRMATION: 'Order confirmation',
};

const termsValidation = {
  required: true,
  transform: value => (value || undefined),
  type: 'boolean',
};

const getRenewalUnit = (interval) => {
  if (interval === LicenseInterval.Yearly) {
    return '/year';
  }
  if (interval === LicenseInterval.Monthly) {
    return '/month';
  }
  return '';
};

const ChangeLicense = ({ open, onCancel, onChange, initialModule = null }) => {
  const { dataSource, loading: dataSourceLoading } = useDataSource({
    extraFields: 'owner { id email } customer { id paymentType } pipedriveDealId',
    activeLicenseFields: 'modules { settings(keys: ["prices"]) } orders { id amount subtotal startDate endDate paymentStatus createdAt currency discount discountType items { relationId amount price quantity type discount discountType } discounts { title amount type calculatedAmount } }',
  });
  const { isSuperUser } = useUser();
  const minStep = isSuperUser ? STEPS.SELECT_LICENSE_TYPE : STEPS.CONFIGURE_LICENSE;
  const [step, setStep] = useState(minStep);
  const [form] = Form.useForm();
  const [licenseForm] = Form.useForm();
  const [orderForm] = Form.useForm();
  const [renewalForm] = Form.useForm();
  const { message } = useGlobalContext();
  const [changeLicense, { loading: changeLicenseLoading }] = useMutation(CHANGE_LICENSE);
  const [licenseType, setLicenseType] = useState();
  const [licenseValues, setLicenseValues] = useState(null);
  const [formOrder, setFormOrder] = useState(null);
  const [renewalOrder, setRenewalOrder] = useState(null);
  const [couponCode, setCouponCode] = useState('');
  const [coupon, setCoupon] = useState(null);
  const [showCouponInput, setShowCouponInput] = useState(false);
  const client = useApolloClient();

  const loading = dataSourceLoading;

  useEffect(() => {
    if (open) {
      setStep(STEPS.CONFIGURE_LICENSE);
      setLicenseType(DEFAULT_LICENSE_TYPE[dataSource?.type]);
      setCoupon(null);
      setCouponCode('');
      setShowCouponInput(false);
    }
  }, [open]);

  const onLicenseTypeSelect = (type) => {
    setLicenseType(type);
    setStep(STEPS.CONFIGURE_LICENSE);
  };

  const onLicenseChanged = (values, activeCoupon = coupon) => {
    setLicenseValues(values);
    const order = createOrder({
      ...values,
      dataSource,
      activeLicense: dataSource?.activeLicense,
      discounts: activeCoupon ? [{
        title: `Coupon: ${activeCoupon.code}`,
        description: activeCoupon.description,
        amount: activeCoupon.discountAmount,
        type: activeCoupon.discountType,
        couponId: activeCoupon.id,
      }] : [],
    });
    setFormOrder(order);
    orderForm.setFieldsValue(order);
    const orderConfig = createOrder({
      ...values,
      dataSource,
      activeLicense: dataSource?.activeLicense,
      startDate: dayjs(order.endDate).add(1, 'day'),
    });
    setRenewalOrder(orderConfig);
    renewalForm.setFieldsValue(orderConfig);
  };

  const onCouponApply = async () => {
    try {
      const { data: couponData } = await client.query({
        query: VALIDATE_COUPON,
        variables: { code: couponCode, customerId: dataSource?.customer.id },
        fetchPolicy: 'network-only',
      });
      if (couponData.validateCoupon) {
        setCoupon(couponData.validateCoupon);
        onLicenseChanged(licenseValues, couponData.validateCoupon);
        message.success('Coupon applied');
      } else {
        message.error('Invalid or expired coupon');
      }
    } catch (error) {
      message.error(error.message);
    }
  };

  const onSubmit = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (error) {
      return;
    }

    if (isMidTermDowngradeBlocked({
      activeLicense: dataSource?.activeLicense,
      startDate: licenseValues?.startDate,
      interval: licenseValues?.interval,
      modules: licenseValues?.modules,
      settings: licenseValues?.settings,
      licenses: licenseValues?.licenses,
      license: licenseValues?.license,
      level: licenseValues?.level,
    }) && !isSuperUser) {
      message.error('You cannot downgrade your license mid term');
      return;
    }

    const orderValues = getCalculatedOrder(orderForm.getFieldsValue(true));
    const orderConfigValues = renewalOrder ? getCalculatedOrder(renewalForm.getFieldsValue(true)) : null;

    if (orderValues?.amount != null) {
      try {
        const result = await changeLicense({
          variables: {
            input: {
              dataSourceId: dataSource.id,
              licenseId: licenseValues.licenseId,
              interval: licenseValues.interval,
              startDate: licenseValues.startDate,
              endDate: licenseValues.endDate,
              modules: licenseValues.modules,
              settings: licenseValues.settings,
              order: orderValues,
              orderConfig: orderConfigValues,
            },
            syncDeal: values.syncDeal,
            orderConfirmation: values.orderConfirmation,
          },
        });
        if (result.data.changeDataSourceLicense) {
          setStep(STEPS.ORDER_CONFIRMATION);
          onChange();
        } else {
          message.error(result.errors[0].message);
        }
      } catch (error) {
        message.error(error.message);
      }
    } else {
      message.info('License combination is not valid');
    }
  };

  const onCustomizeOrder = () => {
    setStep(STEPS.CUSTOMIZE_ORDER);
  };

  const onCustomizeRenewal = () => {
    setStep(STEPS.CUSTOMIZE_RENEWAL);
  };

  const onPaymentSetupSuccess = () => {
    console.log('Payment setup success');
    // orderForm.submit();
  };

  const requiresPaymentSetup = !dataSource?.customer.paymentType;

  const midTermDowngrade = isMidTermDowngradeBlocked({
    activeLicense: dataSource?.activeLicense,
    startDate: licenseValues?.startDate,
    interval: licenseValues?.interval,
    modules: licenseValues?.modules,
    settings: licenseValues?.settings,
    licenses: licenseValues?.licenses,
    license: licenseValues?.license,
    level: licenseValues?.level,
  });

  const canOrder = (!midTermDowngrade || isSuperUser) && (isSuperUser || ((licenseValues?.license || licenseValues?.modules?.length > 0) && !requiresPaymentSetup));

  let currentForm = null;
  if (step === STEPS.CONFIGURE_LICENSE) {
    currentForm = licenseForm;
  } else if (step === STEPS.CUSTOMIZE_ORDER) {
    currentForm = orderForm;
  } else if (step === STEPS.CUSTOMIZE_RENEWAL) {
    currentForm = renewalForm;
  }

  return (
    <Modal
      title="Change license"
      centered
      open={open}
      width={step !== STEPS.ORDER_CONFIRMATION ? 1200 : 800}
      loading={loading}
      footer={step !== STEPS.ORDER_CONFIRMATION && step !== STEPS.SELECT_LICENSE_TYPE ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
          <Space>
            {isSuperUser && (
              <>
                <Button onClick={() => setStep(STEPS.SELECT_LICENSE_TYPE)}>
                  Select license type
                </Button>
                <Button onClick={() => setStep(STEPS.CONFIGURE_LICENSE)}>
                  Configure license
                </Button>
                {formOrder && (
                  <Button onClick={onCustomizeOrder}>
                    Customize order
                  </Button>
                )}
                {renewalOrder && (
                  <Button onClick={onCustomizeRenewal}>
                    Customize renewal
                  </Button>
                )}
              </>
            )}
            {currentForm && (
              <Button type="primary" onClick={currentForm.submit} loading={changeLicenseLoading} disabled={!canOrder}>
                Order
              </Button>
            )}
          </Space>
        </div>
      ) : null}
      onCancel={onCancel}
      maskClosable={false}
    >
      {step === STEPS.SELECT_LICENSE_TYPE && (
        <Row gutter={16}>
          <Col span={6}>
            <Card title="E-Space" hoverable onClick={() => onLicenseTypeSelect('legacy')}>
              All in one license
            </Card>
          </Col>
          <Col span={6}>
            <Card title="May 2023" hoverable onClick={() => onLicenseTypeSelect('v1')}>
              Multiple product levels
            </Card>
          </Col>
          <Col span={6}>
            <Card title="Nov 2024" hoverable onClick={() => onLicenseTypeSelect('v2')}>
              Single product level
            </Card>
          </Col>
          <Col span={6}>
            <Card title="Dec 2025" hoverable onClick={() => onLicenseTypeSelect('v3')}>
              Multiple products and levels
            </Card>
          </Col>
        </Row>
      )}
      {
        step === STEPS.CONFIGURE_LICENSE && (
          <>
            {licenseType === 'legacy' && (
              <LegacyLicenseForm form={licenseForm} dataSource={dataSource} onSubmit={onSubmit} onChange={onLicenseChanged} />
            )}
            {licenseType === 'v1' && (
              <ModuleLicenseForm form={licenseForm} dataSource={dataSource} onSubmit={onSubmit} onChange={onLicenseChanged} />
            )}
            {licenseType === 'v2' && (
              <ModuleLicenseV2Form form={licenseForm} dataSource={dataSource} onSubmit={onSubmit} onChange={onLicenseChanged} />
            )}
            {licenseType === 'v3' && (
              <ModuleLicenseV3Form form={licenseForm} dataSource={dataSource} onSubmit={onSubmit} onChange={onLicenseChanged} initialModule={initialModule} />
            )}
            <Row gutter={16}>
              <Col span={12}>
                <Form form={form} initialValues={{ terms: isSuperUser, orderConfirmation: !isSuperUser, syncDeal: dataSource?.pipedriveDealId != null }}>
                  <Form.Item name="terms" rules={[{ ...termsValidation, message: 'You need to accept the terms of service' }]} valuePropName="checked">
                    <Checkbox>
                      I have read and agree to the&nbsp;
                      <a
                        href={TERMS_OF_SERVICE_NO_PDA}
                        target="_blank"
                      >
                        Terms of service
                      </a>
                    </Checkbox>
                  </Form.Item>
                  {dataSource?.owner && isSuperUser && (
                    <Form.Item name="orderConfirmation" valuePropName="checked" noStyle>
                      <Checkbox>
                        Send order confirmation to data source owner
                        {' '}
                        {dataSource.owner.email}
                      </Checkbox>
                    </Form.Item>
                  )}
                  {isSuperUser && (
                    <Form.Item name="syncDeal" valuePropName="checked" noStyle>
                      <Checkbox>
                        Sync to Pipedrive
                      </Checkbox>
                    </Form.Item>
                  )}
                </Form>
              </Col>

              <Col span={12}>
                {formOrder && (
                  <>
                    {!coupon && !showCouponInput ? (
                      <div style={{ marginBottom: 8, textAlign: 'right' }}>
                        <Button type="link" size="small" onClick={() => setShowCouponInput(true)} style={{ padding: 0 }}>
                          Add discount coupon
                        </Button>
                      </div>
                    ) : (
                      <div style={{ marginBottom: 8, textAlign: 'right' }}>
                        <Space>
                          <Input
                            placeholder="Coupon code"
                            value={couponCode}
                            onChange={e => setCouponCode(e.target.value)}
                            disabled={!!coupon}
                            size="small"
                            style={{ width: 150 }}
                          />
                          {!coupon ? (
                            <Button size="small" onClick={onCouponApply} disabled={!couponCode}>Apply</Button>
                          ) : (
                            <Button size="small" onClick={() => { setCoupon(null); onLicenseChanged(licenseValues, null); }}>Remove</Button>
                          )}
                        </Space>
                      </div>
                    )}
                    <Order order={formOrder} hidePeriod hideDataSource />
                    {renewalOrder && renewalOrder.amount !== formOrder.amount && (
                      <div style={{ textAlign: 'right', marginTop: 8 }}>
                        <small>
                          {`Renews at ${currencyFormat(renewalOrder.amount, { currency: renewalOrder.currency })}${getRenewalUnit(licenseValues?.interval)}`}
                        </small>
                      </div>
                    )}
                  </>
                )}
              </Col>
            </Row>
            {requiresPaymentSetup && (
              <Alert
                message="Setup payment before you can order a license."
                type="warning"
                showIcon
                action={<SetupPaymentButton customerId={dataSource?.customer.id} onSuccess={onPaymentSetupSuccess} />}
                style={{ margin: '16px 0' }}
              />
            )}
            {midTermDowngrade && (
              <Alert
                message="You cannot downgrade your license mid term."
                type="error"
                showIcon
                style={{ margin: '16px 0' }}
              />
            )}
          </>
        )
      }
      {
        step === STEPS.CUSTOMIZE_ORDER && (
          <OrderForm form={orderForm} onSubmit={onSubmit} currency={licenseValues?.currency} />
        )
      }
      {
        step === STEPS.CUSTOMIZE_RENEWAL && (
          <OrderForm form={renewalForm} onSubmit={onSubmit} currency={licenseValues?.currency} />
        )
      }
      {
        step === STEPS.ORDER_CONFIRMATION && (
          <Result
            status="success"
            title="License changed"
            subTitle={(
              <p>
                Your license has been updated.
              </p>
            )}
          />
        )
      }
    </Modal>
  );
};

export default ChangeLicense;
